import React, { useState, useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import ChatComponent from "./ChatComponent";
import UpgradeAccountModal from "./components/UpgradeAccountModal";
import WelcomeScreen from "./components/WelcomeScreen";
import OutcomeFlowPreview from "./components/OutcomeFlowPreview";
import OutcomeLanding from "./components/OutcomeLanding";
import ProductAnalysisView from "./components/ProductAnalysisView";
import ImageConsentModal from "./components/ImageConsentModal";
import ContextualSaveInvitation, { RegistrationDismissReason } from "./components/ContextualSaveInvitation";
import EmailJourneyEntry, { getEmailJourney } from "./components/EmailJourneyEntry";
import { initAnalytics, trackAppSessionStart, trackAppSessionEnd } from "./utils/analytics";
import ColourAnalysisExperience from "./features/colour-analysis/ColourAnalysisExperience";
import ColourAnalysisV1 from "./features/colour-analysis/ColourAnalysisV1";
import ColourCatalogueCheck from "./features/colour-analysis/ColourCatalogueCheck";

function SaveInvitationPreview({ initialStage = 'nudge' }) {
  const [stage, setStage] = useState(initialStage);
  const continueExploring = () => setStage('continued');
  const hardGate = stage === 'hard' || stage === 'hard-closed';

  return (
    <div style={{ minHeight: '100vh', background: '#FFFDFC', padding: '72px 24px 140px', fontFamily: 'DM Sans, sans-serif' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <p style={{ color: '#A96F54', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, fontSize: 13 }}>Your conversation</p>
        <h1 style={{ color: '#341C1C', fontFamily: 'var(--font-sonmi-display)', fontSize: 48, lineHeight: 1, margin: '12px 0 24px' }}>Here’s what I’d try</h1>
        <div style={{ border: '1px solid #E4D9D0', borderRadius: 24, padding: 24, background: '#FFFFFF', color: '#5F514B', lineHeight: 1.6 }}>
          Your skin looks dehydrated rather than dry, so I’d simplify the routine first and watch how it responds for two weeks.
        </div>
        {hardGate ? (
          <button type="button" onClick={() => setStage('hard')} style={{ width: '100%', marginTop: 14, border: 0, borderRadius: 999, padding: '15px 18px', background: '#341C1C', color: '#FFFFFF', fontWeight: 700 }}>
            Create a free account to keep chatting
          </button>
        ) : (
          <div style={{ marginTop: 14, border: '1px solid #E4D9D0', borderRadius: 999, padding: '15px 18px', background: '#FFFFFF', color: '#9A8D86' }}>Type your next question…</div>
        )}
        {stage === 'continued' && (
          <div role="status" style={{ marginTop: 16, borderRadius: 16, padding: '13px 16px', background: '#F3E5D9', color: '#704330', fontSize: 14 }}>
            Thanks — keep exploring.
          </div>
        )}
      </div>
      {stage === 'nudge' && <ContextualSaveInvitation onDismiss={() => setStage('reason')} />}
      {stage === 'reason' && <RegistrationDismissReason onSelect={continueExploring} onSkip={continueExploring} />}
      {stage === 'hard' && (
        <ContextualSaveInvitation
          variant="hard"
          onDismiss={() => setStage('hard-closed')}
          onCreateAccount={() => setStage('account')}
          onSignIn={() => setStage('account-signin')}
        />
      )}
      <UpgradeAccountModal
        isOpen={stage === 'account' || stage === 'account-signin'}
        startOnSignIn={stage === 'account-signin'}
        skipPitch
        mandatory
        onClose={() => setStage('hard-closed')}
      />
    </div>
  );
}

function SetNewPasswordScreen() {
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    const { error } = await updatePassword(password);
    if (error) { setError(error.message); setLoading(false); }
    else setDone(true);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F2EAE1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 16, padding: '2rem', maxWidth: 400, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <h2 style={{ fontFamily: '"DM Sans", Arial, sans-serif', fontWeight: 700, color: '#341C1C', marginBottom: 8, fontSize: 20 }}>
          Set New Password
        </h2>
        {done ? (
          <p style={{ color: '#155724', fontFamily: '"DM Sans", Arial, sans-serif', fontSize: 14, background: '#d4edda', padding: 12, borderRadius: 8 }}>
            Password updated! You're now signed in. Return to the app.
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            <p style={{ color: '#2A324B', fontSize: 14, fontFamily: '"DM Sans", Arial, sans-serif', marginBottom: 20, opacity: 0.6 }}>
              Choose a strong new password for your account.
            </p>
            {['New Password', 'Confirm Password'].map((label, i) => (
              <div key={label} style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#341C1C', fontFamily: '"DM Sans", Arial, sans-serif', marginBottom: 6 }}>
                  {label}
                </label>
                <input
                  type="password"
                  value={i === 0 ? password : confirm}
                  onChange={e => i === 0 ? setPassword(e.target.value) : setConfirm(e.target.value)}
                  minLength={6}
                  required
                  style={{ width: '100%', padding: 12, border: '1px solid #e1e5e9', borderRadius: 8, fontSize: 15, fontFamily: '"DM Sans", Arial, sans-serif', boxSizing: 'border-box' }}
                  placeholder={i === 0 ? 'At least 6 characters' : 'Repeat password'}
                />
              </div>
            ))}
            {error && (
              <div style={{ color: '#dc3545', fontSize: 13, background: '#f8d7da', padding: 10, borderRadius: 6, marginBottom: 16, fontFamily: '"DM Sans", Arial, sans-serif' }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              style={{ width: '100%', padding: 13, background: loading ? '#6c757d' : '#341C1C', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, fontFamily: '"DM Sans", Arial, sans-serif', cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Saving…' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// Main app component that handles auth state
function AppContent() {
  const { user, loading, signOut, isAnonymous, needsPasswordReset } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [startOnSignIn, setStartOnSignIn] = useState(false);
  const sessionStartRef = useRef(null);
  const wasRegisteredRef = useRef(false);
  const emailJourney = getEmailJourney(window.location.search);
  const emailJourneyKey = emailJourney?.key || null;
  const [emailJourneyStarted, setEmailJourneyStarted] = useState(false);
  const [emailJourneyGuest, setEmailJourneyGuest] = useState(false);

  // Init analytics and fire session start once user is resolved
  useEffect(() => {
    if (!user || loading) return;

    initAnalytics(user.id, isAnonymous);

    // Only fire app_session_start once per browser tab
    const sessionKey = 'sonmi_session_tracked';
    if (!sessionStorage.getItem(sessionKey)) {
      sessionStorage.setItem(sessionKey, '1');
      trackAppSessionStart();
      sessionStartRef.current = Date.now();
    }
  }, [user, loading, isAnonymous]);

  // Track session duration when the tab closes
  useEffect(() => {
    const handleUnload = () => {
      if (sessionStartRef.current) {
        const durationSeconds = Math.round((Date.now() - sessionStartRef.current) / 1000);
        trackAppSessionEnd(durationSeconds);
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Sign-out is now the only "a new guest is starting" moment (the old
  // "Continue as guest" button that used to reset guest state is gone with the
  // WelcomeScreen). Wipe guest-scoped localStorage on the registered→anonymous
  // transition so the next person on this device doesn't inherit the last
  // user's chat list. See resetGuestState below.
  useEffect(() => {
    if (!isAnonymous) {
      wasRegisteredRef.current = true;
      return;
    }
    if (wasRegisteredRef.current) {
      wasRegisteredRef.current = false;
      resetGuestState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnonymous]);

  // Handle continuing as guest.
  // Guests get onboarding on EVERY fresh guest entry (not just once per browser):
  // clear the "has seen tour" flag here so the FirstTimeOverview fires when
  // ChatComponent mounts. The tour re-sets the flag on complete/skip, so it won't
  // re-loop within the same session — only a new "Continue as guest" re-shows it.
  //
  // 2026-06-04 (TikTok demo / event fix): the previous guest's chat history was
  // bleeding through to the next guest because we share the same anonymous
  // Supabase user across guest sessions, and sonmi_chats_${userId} /
  // sonmi_current_session_${userId} persist their chat list + active session id.
  // Wipe all guest-state localStorage so the next mount of ChatComponent starts
  // a brand-new session id (→ empty chat history fetched from backend → fresh
  // welcome message). Backend rows from the previous session stay in the DB
  // (untouched) but are no longer reachable from this device's UI.
  const resetGuestState = () => {
    try {
      // Auth-/profile-related
      localStorage.removeItem('sonmi_image_consent');
      localStorage.removeItem('sonmi_image_consent_at');
      localStorage.removeItem('sonmi_guest_profile');
      localStorage.removeItem('sonmi_had_analysis');
      // Recently-analysed is now keyed by userId (so a real user's list persists
      // across their logout/login). On guest entry clear only the shared anon
      // user's list + the legacy unkeyed key — real users' keyed lists stay intact.
      localStorage.removeItem('sonmi_recent_analyses');
      localStorage.removeItem('sonmi_recent_analyses_guest');
      if (user?.id) localStorage.removeItem('sonmi_recent_analyses_' + user.id);
      // Chat-list + current-session keys are user-id-scoped, so iterate and
      // wipe every sonmi_chats_* / sonmi_current_session_* / sonmi_first_session_*
      // entry on this device. Cheap; runs once on guest entry.
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (
          k.startsWith('sonmi_chats_') ||
          k.startsWith('sonmi_current_session_') ||
          k.startsWith('sonmi_first_session_') ||
          k.startsWith('sonmi_messages_')
        ) {
          toRemove.push(k);
        }
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch {}
  };

  // /?open=welcome shows the editorial welcome screen, the one the native app
  // still opens on. Used by the climate email variant for people whose climate
  // Son:mi already holds: they all have accounts and none has signed in for
  // over a month, so Sign in leads and Create your account stays as the second
  // option. Continue as guest drops them into chat as normal.
  const [showWelcome, setShowWelcome] = useState(
    () => new URLSearchParams(window.location.search).get('open') === 'welcome'
  );

  // /?open=signin opens straight into the sign-in screen. Used by the climate
  // email variant sent to people whose climate Son:mi already holds: that email
  // says Son:mi knows where they are, which is only true once they are signed
  // in, so landing them anonymously would break the promise the email just made.
  //
  // Every one of those recipients already has an account and none has signed in
  // for over a month, so this is a return rather than a new hurdle.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('open') !== 'signin') return;
    if (!isAnonymous) return; // already signed in, nothing to ask for
    setStartOnSignIn(true);
    setShowAuthModal(true);
  }, [isAnonymous]);

  // Handle successful auth
  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    if (emailJourney) setEmailJourneyStarted(true);
  };

  // Preserve the email CTA intent across authentication. Registered visitors
  // skip the selector and go directly to the requested experience.
  useEffect(() => {
    if (!loading && emailJourneyKey && !isAnonymous) setEmailJourneyStarted(true);
  }, [loading, emailJourneyKey, isAnonymous]);

  // Password reset deep-link — user clicked the email reset link
  if (needsPasswordReset) {
    return <SetNewPasswordScreen />;
  }

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "#F2EAE1",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "18px",
        color: "#341C1C",
        fontFamily: "'DM Sans', Arial, sans-serif"
      }}>
        Loading Son:mi...
      </div>
    );
  }

  // If somehow no user, show loading state (shouldn't happen with anonymous auth)
  if (!user) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "#F2EAE1",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "18px",
        color: "#341C1C",
        fontFamily: "'DM Sans', Arial, sans-serif"
      }}>
        Setting up Son:mi...
      </div>
    );
  }

  // Arrivals from an email CTA skip the generic intro. They have already told us
  // what they came for by clicking a button about it, and making them answer
  // "what are you hoping to figure out today?" first would stack two
  // onboarding screens before any content. Only affects links carrying ?open=,
  // so ordinary first-time visitors are untouched.
  if (emailJourney && isAnonymous && !emailJourneyStarted) {
    return (
      <>
        <EmailJourneyEntry
          journey={emailJourney}
          onStartNew={() => {
            if (emailJourney.requiresAccount) {
              setStartOnSignIn(false);
              setShowAuthModal(true);
              return;
            }
            setEmailJourneyGuest(true);
            setEmailJourneyStarted(true);
          }}
          onSignIn={() => {
            setStartOnSignIn(true);
            setShowAuthModal(true);
          }}
        />
        <UpgradeAccountModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
          startOnSignIn={startOnSignIn}
        />
      </>
    );
  }
  if (showWelcome && isAnonymous) {
    return (
      <>
        <WelcomeScreen
          returning
          onSignIn={() => { setStartOnSignIn(true); setShowAuthModal(true); }}
          onCreateAccount={() => { setStartOnSignIn(false); setShowAuthModal(true); }}
          onContinueAsGuest={() => setShowWelcome(false)}
        />
        <UpgradeAccountModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => { setShowWelcome(false); handleAuthSuccess(); }}
          startOnSignIn={startOnSignIn}
          skipPitch
        />
      </>
    );
  }

  // Show chat for all users (anonymous or registered)
  return (
    <div style={{ minHeight: "100vh", background: "#F2EAE1" }}>
      <ChatComponent
        user={user}
        signOut={signOut}
        isAnonymous={isAnonymous}
        autoStartScan={Boolean(emailJourney && (!isAnonymous || emailJourneyStarted))}
        saveAfterFirstScan={Boolean(emailJourneyGuest)}
      />
      {/* Also rendered here, not only in the introductory screen above. Without
          this, /?open=signin set showAuthModal on a screen that never rendered
          the modal, so the deep link silently did nothing. */}
      <UpgradeAccountModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        startOnSignIn={startOnSignIn}
        skipPitch
      />
    </div>
  );
}

// App main export with Auth Provider
export default function App() {
  if (process.env.REACT_APP_COLOUR_STUDY_ONLY === 'true') {
    return <AuthProvider><ColourAnalysisV1 /></AuthProvider>;
  }
  const params = new URLSearchParams(window.location.search);
  if (process.env.REACT_APP_ENABLE_COLOUR_ANALYSIS === 'true'
    && window.location.hostname !== 'my-app-three-murex.vercel.app'
    && params.get('feature') === 'colour-analysis'
    && params.get('view') === 'catalogue-check') {
    return <ColourCatalogueCheck />;
  }
  if (process.env.REACT_APP_ENABLE_COLOUR_ANALYSIS === 'true' && params.get('feature') === 'colour-analysis') {
    return <AuthProvider><ColourAnalysisV1 /></AuthProvider>;
  }
  const preview = process.env.NODE_ENV !== 'production' ? params.get('preview') : null;
  if (preview === 'colour-analysis') {
    const requestedScreen = params.get('screen');
    const studyMode = params.get('study') === '1';
    const allowedScreens = ['intro', 'prepare', 'guided', 'profile', 'palette', 'study-start'];
    const initialScreen = allowedScreens.includes(requestedScreen) ? requestedScreen : studyMode ? 'study-start' : 'intro';
    return <ColourAnalysisExperience preview studyMode={studyMode} initialScreen={initialScreen} />;
  }
  const showOutcomePreview = preview === 'journeys';
  if (showOutcomePreview) return <OutcomeFlowPreview />;
  if (preview === 'outcome-intro') {
    return <WelcomeScreen exploreFirst onContinueAsGuest={() => {}} onSignIn={() => {}} />;
  }
  // Auth-free rendering of the exact landing component used by ChatUI. This is
  // intentionally local-only so visual verification cannot create a guest or
  // touch the shared Supabase project.
  if (preview === 'outcome-onboarding') {
    return <div style={{ minHeight: '100vh', background: '#FFFFFF', padding: '24px 18px' }}><OutcomeLanding /></div>;
  }
  if (preview === 'consent') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2EAE1' }}>
        <ImageConsentModal isOpen onAllow={() => {}} onNotNow={() => {}} />
      </div>
    );
  }
  if (preview === 'save-invitation') {
    const stage = new URLSearchParams(window.location.search).get('stage') === 'hard' ? 'hard' : 'nudge';
    return <SaveInvitationPreview initialStage={stage} />;
  }
  if (preview === 'product-analysis') {
    const previewResult = {
      product: {
        name: 'Hydrating Barrier Serum', brand: 'Example', category: 'Serum',
        keyIngredients: ['Ceramides', 'Panthenol', 'Niacinamide'],
        formulation_bullets: [
          { ingredient: 'Ceramides', note: 'support the skin barrier' },
          { ingredient: 'Panthenol', note: 'adds soothing hydration' },
        ],
      },
      matchScore: 82,
      scoring: {
        concerns: { assessed: true, score: 84, reason: 'Supports dryness and barrier care.' },
        skin_type: { assessed: true, score: 80, reason: 'A flexible serum texture.' },
        formulation: { assessed: true, score: 82, reason: 'A balanced hydrating formula.' },
      },
      analysis: { pros: ['Hydrating, barrier-supportive base'], cons: ['Patch test if niacinamide is new to you'] },
      recommendation: { usage: 'Apply after cleansing and before moisturiser.' },
    };
    return <div style={{ height: '100vh', maxWidth: 520, margin: '0 auto', background: '#FFFFFF' }}><ProductAnalysisView existingProduct={{ image: '/journey-icons/specific-product.webp' }} previewResult={previewResult} onBack={() => window.history.back()} /></div>;
  }
  if (preview === 'email-journey') {
    return <EmailJourneyEntry journey={getEmailJourney('?open=skin-type')} onSignIn={() => {}} onStartNew={() => {}} />;
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
